<template>
  <svg :viewBox="viewBox" preserveAspectRatio="xMinYMin meet">
    <rect
      class="deck-hist-svg-bg"
      :height="height"
      :width="width"
      y="0"
      :stroke-width="gridWeight"
    ></rect>
    <line
      class="deck-hist-svg-gridline"
      v-for="n in height - 1"
      x1="0"
      :x2="width"
      :y1="height - n"
      :y2="height - n"
      :key="'gridY_' + n"
      :stroke-width="n % 5 ? gridWeight / 2 : gridWeight"
    />
    <line
      class="deck-hist-svg-gridline"
      v-for="n in width - 1"
      :x1="n"
      :x2="n"
      y1="0"
      :y2="height"
      :key="'gridX_' + n"
      :stroke-width="n % 5 ? gridWeight / 2 : gridWeight"
    />
    <template v-for="(card, index) in sortedCards">
      <line
        class="deck-hist-divider"
        :class="card.type"
        v-for="n in card.quantity - 1"
        :stroke-width="gridWeight / 2"
        :x1="card.xPos + n"
        :x2="card.xPos + n"
        :y1="height - (card.value ? card.value : gridWeight)"
        :y2="height"
        :key="`card-${index}gridLine-${n}`"
      />
      <rect
        class="deck-hist-rect"
        :class="card.type"
        :key="$id('card-' + index)"
        :stroke-width="gridWeight"
        :width="card.quantity - gridWeight / 2"
        :height="card.value ? card.value : gridWeight"
        :x="card.xPos + gridWeight / 4"
        :y="height - (card.value ? card.value : gridWeight)"
        :id="$id('card-' + index)"
        tabindex="0"
      />
      <line
        class="deck-hist-svg-boostline"
        :key="$id('card-boost-' + index)"
        :x1="card.xPos + gridWeight / 4"
        :x2="card.xPos + gridWeight / 4 + card.quantity - gridWeight / 2"
        :y1="height - (card.boost ? card.boost : gridWeight)"
        :y2="height - (card.boost ? card.boost : gridWeight)"
        :stroke-width="gridWeight"
        :stroke-dasharray="gridWeight"
      />
      <b-popover
        :key="'popover-' + index"
        :target="$id('card-' + index)"
        triggers="hover focus"
        placement="top"
      >
        <template v-slot:title>{{ card.title }}</template>
        <strong>{{ card.characterName }}</strong>
        <p>
          {{ card.type.charAt(0).toUpperCase() + card.type.slice(1) }}
          {{ card.type === "scheme" ? "" : card.value }}
          <span class="float-right ml-2">Boost {{ card.boost }}</span>
        </p>
        <p v-if="card.basicText">
          {{ card.basicText }}
        </p>
        <p v-if="card.immediateText">
          <strong>Immediately:</strong>
          {{ card.immediateText }}
        </p>
        <p v-if="card.duringText">
          <strong>During combat:</strong>
          {{ card.duringText }}
        </p>
        <p v-if="card.afterText">
          <strong>After combat:</strong>
          {{ card.afterText }}
        </p>
        <p class="float-right">&times;{{ card.quantity }}</p>
      </b-popover>
    </template>
  </svg>
</template>

<script type="text/javascript">
import { UnmatchedCard } from "@/Unmatched";

export default {
  name: "DeckHistogram",
  props: {
    cards: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      width: 30,
      height: 8,
      gridWeight: 0.1,
      majorLine: 5,
      minorLine: 1,
    };
  },
  computed: {
    viewBox() {
      return `-1 -1 ${this.width + 2} ${this.height + 2}`;
    },
    sortedCards() {
      const typeSort = {
        attack: 0,
        versatile: 1,
        defence: 2,
        defense: 2,
        scheme: 3,
      };

      var xPos = 0;
      return this.cards
        .concat()
        .sort((cardA, cardB) => {
          if (cardA.type === cardB.type) {
            return cardA.value - cardB.value;
          }
          return typeSort[cardA.type] - typeSort[cardB.type];
        })
        .map((card) => {
          const positionedCard = new UnmatchedCard(card);
          positionedCard.xPos = xPos;
          if (card.type.toLocaleLowerCase() === "scheme")
            positionedCard.value = 1;
          xPos += card.quantity;
          return positionedCard;
        });
    },
  },
  methods: {
    typeColour(type) {
      switch (type.toLocaleLowerCase()) {
        case "scheme":
          return "#fcbd71";
        case "defence":
        case "defense":
          return "#2c76ac";
        case "attack":
          return "#dc3034";
        case "versatile":
          return "#6c4e8d";
        default:
          return "gray";
      }
    },
  },
};
</script>

<style lang="less">
@defence-blue: #2c76ac;
@attack-red: #dc3034;
@versatile-violet: #6c4e8d;
@scheme-yellow: #fcbd71;
.deck-hist-rect,
.deck-hist-divider {
  &:focus {
    outline: none;
  }
  &.scheme {
    fill: fade(@scheme-yellow, 50%);
    stroke: @scheme-yellow;
    &:focus,
    &:hover {
      stroke: lighten(@scheme-yellow, 20%);
      fill: lighten(fade(@scheme-yellow, 50%), 20%);
    }
  }
  &.attack {
    fill: fade(@attack-red, 50%);
    stroke: @attack-red;
    &:focus,
    &:hover {
      stroke: lighten(@attack-red, 20%);
      fill: lighten(fade(@attack-red, 50%), 20%);
    }
  }
  &.defence,
  &.defense {
    fill: fade(@defence-blue, 50%);
    stroke: @defence-blue;
    &:focus,
    &:hover {
      stroke: lighten(@defence-blue, 20%);
      fill: lighten(fade(@defence-blue, 50%), 20%);
    }
  }
  &.versatile {
    fill: fade(@versatile-violet, 50%);
    stroke: @versatile-violet;
    &:focus,
    &:hover {
      stroke: lighten(@versatile-violet, 20%);
      fill: lighten(fade(@versatile-violet, 50%), 20%);
    }
  }
}
.deck-hist-svg-bg {
  fill: #eee;
  @media (prefers-color-scheme: dark) {
    fill: #222;
  }
}

.deck-hist-svg-gridline {
  stroke: #fff;
  @media (prefers-color-scheme: dark) {
    stroke: #333;
  }
}

.deck-hist-svg-boostline {
  stroke: #000;
  @media (prefers-color-scheme: dark) {
    stroke: #fff;
  }
}
</style>
